import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { Target, Warning, Lightbulb, PaperPlaneTilt, X, CheckCircle, CaretRight, User } from "@phosphor-icons/react";

/**
 * WeeklyReport — Sunday-focused dedicated page listing every client's
 * pattern findings for the week with one-tap DM send buttons. Replaces the
 * always-visible Pattern Spotter cards that used to clutter the coach's
 * daily views. Sunday 6pm push notification deep-links here.
 */
const SEVERITY_BORDER = { 3: "border-[#FF3333]", 2: "border-orange-500/60", 1: "border-yellow-500/40" };
const SEVERITY_TEXT = { 3: "text-[#FF3333]", 2: "text-orange-400", 1: "text-yellow-400" };
const SEVERITY_BG = { 3: "bg-[#FF3333]/5", 2: "bg-orange-500/5", 1: "bg-yellow-500/5" };
const SEVERITY_LABEL = { 3: "High", 2: "Medium", 1: "Low" };

export default function WeeklyReport() {
  const [data, setData] = useState(null);
  const [byClient, setByClient] = useState({}); // clientId -> [patterns]
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [dmDraft, setDmDraft] = useState(null); // {clientId, pattern, text}
  const [dmSending, setDmSending] = useState(false);
  const [dmSent, setDmSent] = useState(false);
  const [dmErr, setDmErr] = useState("");
  const [sentClientPatterns, setSentClientPatterns] = useState(new Set());

  const reload = async () => {
    setErr("");
    try {
      const { data } = await http.get("/coach/patterns");
      setData(data);
      const detailed = {};
      for (const row of data.results) {
        try {
          const r = await http.get(`/coach/clients/${row.client_id}/patterns`);
          detailed[row.client_id] = r.data.patterns;
        } catch { detailed[row.client_id] = [row.top_pattern]; }
      }
      setByClient(detailed);
    } catch (e) { setErr(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); reload(); }, []);

  const openDm = (clientId, p) => {
    setDmDraft({ clientId, pattern: p, text: p.dm_template || "" });
    setDmSent(false); setDmErr("");
  };
  const closeDm = () => { setDmDraft(null); setDmSending(false); setDmSent(false); setDmErr(""); };
  const sendDm = async () => {
    if (!dmDraft || !dmDraft.text.trim()) return;
    setDmSending(true); setDmErr("");
    try {
      await http.post(`/messages/${dmDraft.clientId}`, { body: dmDraft.text.trim() });
      // Mark actioned for this ISO week — the finding disappears from the
      // report so the coach can burn down the list without re-DMing.
      try {
        await http.post("/coach/patterns/mark-actioned", {
          client_id: dmDraft.clientId,
          pattern_kind: dmDraft.pattern.kind,
          pattern_title: dmDraft.pattern.title,
        });
      } catch { /* non-fatal — the DM went through, actioned state is best-effort */ }
      setDmSent(true);
      setSentClientPatterns((prev) => new Set(prev).add(`${dmDraft.clientId}:${dmDraft.pattern.kind}:${dmDraft.pattern.title}`));
      // Refetch so the actioned finding drops out of the list.
      setTimeout(() => { closeDm(); reload(); }, 1200);
    } catch (e) { setDmErr(apiError(e, "Send failed.")); }
    finally { setDmSending(false); }
  };

  const markActionedManually = async (clientId, p) => {
    if (!window.confirm("Mark this pattern as actioned? (Use this if you already DM'd them elsewhere.) It'll come back next week if the pattern persists.")) return;
    try {
      await http.post("/coach/patterns/mark-actioned", {
        client_id: clientId, pattern_kind: p.kind, pattern_title: p.title,
      });
      reload();
    } catch (e) { setErr(apiError(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="weekly-report-page">
      <div className="id-label mb-3 flex items-center gap-2"><Target size={12} weight="fill" /> — Weekly report —</div>
      <h1 className="id-h1 mb-2">Sunday Patterns</h1>
      <p className="text-zinc-400 font-manrope text-sm max-w-2xl mb-8">
        Every client with a repeating miss pattern from the last {data?.lookback_weeks || 4} weeks. Blast the pre-written DMs, done for the week.
      </p>

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-4" data-testid="weekly-report-error">{err}</div>}
      {loading && <div className="text-xs text-zinc-500 font-manrope">Loading patterns…</div>}

      {/* Progress bar — burn-down counter for the week */}
      {data && !loading && data.total_count > 0 && (
        <div className="mb-6" data-testid="weekly-report-progress">
          <div className="flex items-center justify-between text-[10px] font-manrope uppercase tracking-[0.15em] text-zinc-500 mb-1.5">
            <span>Actioned this week</span>
            <span className={data.actioned_count === data.total_count ? "text-[#00CC66] font-bold" : "text-zinc-400"}>
              {data.actioned_count} / {data.total_count}
            </span>
          </div>
          <div className="h-2 bg-[#1A1A1A] border border-[#262626] overflow-hidden">
            <div
              className={data.actioned_count === data.total_count ? "h-full bg-[#00CC66] transition-all" : "h-full bg-[#FF3333] transition-all"}
              style={{ width: `${data.total_count ? (data.actioned_count / data.total_count) * 100 : 0}%` }}
              data-testid="weekly-report-progress-bar"
            />
          </div>
        </div>
      )}

      {data && !loading && data.results.length === 0 && (
        <div className="id-card text-center py-10" data-testid="weekly-report-empty">
          <CheckCircle size={40} weight="fill" className="text-[#00CC66] mx-auto mb-3" />
          <div className="font-oswald text-2xl uppercase text-white mb-1">
            {data.total_count > 0 ? "Done for the week" : "All clean this week"}
          </div>
          <div className="text-sm text-zinc-400 font-manrope">
            {data.total_count > 0
              ? `All ${data.total_count} pattern${data.total_count === 1 ? "" : "s"} actioned. Enjoy your Sunday.`
              : "No repeating miss patterns detected across the roster. Enjoy your Sunday."}
          </div>
        </div>
      )}

      {data && !loading && data.results.length > 0 && (
        <div className="space-y-6">
          {data.results.map((row) => {
            const patterns = byClient[row.client_id] || [row.top_pattern];
            return (
              <div key={row.client_id} className="id-card" data-testid={`weekly-report-client-${row.client_id}`}>
                <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-[#262626]">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 bg-[#1A1A1A] border border-[#262626] flex items-center justify-center shrink-0">
                      <User size={16} weight="bold" className="text-zinc-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-oswald text-xl uppercase tracking-wide text-white truncate">{row.client_name}</div>
                      <div className="text-[10px] font-manrope uppercase tracking-[0.15em] text-zinc-500">
                        {patterns.length} pattern{patterns.length === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/coach/clients/${row.client_id}`}
                    className="hidden sm:inline-flex items-center gap-1 text-[10px] font-manrope uppercase tracking-[0.15em] text-zinc-500 hover:text-white"
                    data-testid={`weekly-report-open-${row.client_id}`}
                  >
                    Open profile <CaretRight size={11} weight="bold" />
                  </Link>
                </div>
                <ul className="space-y-3">
                  {patterns.map((p, i) => {
                    const sentKey = `${row.client_id}:${p.kind}:${p.title}`;
                    const isSent = sentClientPatterns.has(sentKey);
                    return (
                      <li
                        key={`${p.kind}-${i}`}
                        className={"border-l-2 pl-3 py-2 " + (SEVERITY_BORDER[p.severity] || SEVERITY_BORDER[2]) + " " + (SEVERITY_BG[p.severity] || SEVERITY_BG[2])}
                        data-testid={`weekly-pattern-${row.client_id}-${p.kind}-${i}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <Warning size={12} weight="fill" className={(SEVERITY_TEXT[p.severity] || SEVERITY_TEXT[2]) + " shrink-0"} />
                            <div className={"text-sm font-bold truncate " + (SEVERITY_TEXT[p.severity] || SEVERITY_TEXT[2])}>{p.title}</div>
                          </div>
                          <span className={"text-[9px] uppercase tracking-[0.15em] font-manrope shrink-0 " + (SEVERITY_TEXT[p.severity] || SEVERITY_TEXT[2])}>
                            {SEVERITY_LABEL[p.severity] || "Med"}
                          </span>
                        </div>
                        <div className="text-xs font-manrope text-zinc-300 mb-1">{p.detail}</div>
                        <div className="flex items-start gap-1.5 text-xs font-manrope text-zinc-400 mt-1.5">
                          <Lightbulb size={11} weight="fill" className="text-yellow-500 shrink-0 mt-0.5" />
                          <div className="italic">{p.suggestion}</div>
                        </div>
                        {p.dm_template && (
                          <div className="mt-2.5 flex items-center gap-3 flex-wrap">
                            <button
                              onClick={() => openDm(row.client_id, p)}
                              disabled={isSent}
                              className={
                                "inline-flex items-center gap-1.5 text-[10px] font-oswald uppercase tracking-[0.15em] font-bold px-2.5 py-1.5 transition-colors " +
                                (isSent
                                  ? "bg-[#00CC66]/10 text-[#00CC66] border border-[#00CC66]/50 cursor-default"
                                  : "bg-[#FF3333] hover:bg-[#E62E2E] text-white")
                              }
                              data-testid={`weekly-dm-btn-${row.client_id}-${p.kind}-${i}`}
                            >
                              {isSent ? (
                                <><CheckCircle size={11} weight="fill" /> Message sent</>
                              ) : (
                                <><PaperPlaneTilt size={11} weight="fill" /> Send suggested message</>
                              )}
                            </button>
                            {!isSent && (
                              <button
                                onClick={() => markActionedManually(row.client_id, p)}
                                className="text-[10px] font-manrope uppercase tracking-[0.15em] text-zinc-500 hover:text-white underline underline-offset-2"
                                data-testid={`weekly-manual-mark-${row.client_id}-${p.kind}-${i}`}
                                title="Use this if you already DM'd them elsewhere"
                              >
                                Already handled
                              </button>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* DM composer modal */}
      {dmDraft && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" onClick={closeDm} data-testid="weekly-dm-modal">
          <div className="bg-[#0A0A0A] border border-[#262626] w-full sm:max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[#262626] px-4 py-3">
              <div>
                <div className="id-label text-[#FF3333]">— Suggested DM —</div>
                <div className="font-oswald text-lg text-white uppercase tracking-tight mt-0.5">{dmDraft.pattern.title}</div>
              </div>
              <button onClick={closeDm} className="text-zinc-500 hover:text-white" aria-label="Close" data-testid="weekly-dm-close">
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="p-4">
              <div className="id-label text-zinc-500 mb-2">Edit before sending</div>
              <textarea
                value={dmDraft.text}
                onChange={(e) => setDmDraft({ ...dmDraft, text: e.target.value })}
                rows={6}
                className="w-full bg-[#0F0F0F] border border-[#262626] text-white text-sm font-manrope px-3 py-2 focus:border-[#FF3333] outline-none resize-none"
                disabled={dmSending || dmSent}
                data-testid="weekly-dm-textarea"
              />
              {dmErr && <div className="mt-2 text-xs text-[#FF3333]">{dmErr}</div>}
              {dmSent && <div className="mt-2 text-xs text-[#00CC66] flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Sent — client got a push.</div>}
              <div className="flex items-center justify-between gap-2 mt-3">
                <button onClick={closeDm} disabled={dmSending} className="text-[10px] font-manrope uppercase tracking-[0.15em] text-zinc-500 hover:text-white disabled:opacity-40">
                  Cancel
                </button>
                <button
                  onClick={sendDm}
                  disabled={dmSending || dmSent || !dmDraft.text.trim()}
                  className={
                    "inline-flex items-center gap-1.5 text-xs font-oswald uppercase tracking-[0.15em] font-bold px-3 py-2 transition-colors " +
                    (dmSent
                      ? "bg-[#00CC66]/10 text-[#00CC66] border border-[#00CC66]/50 cursor-default"
                      : "bg-[#FF3333] hover:bg-[#E62E2E] text-white disabled:opacity-40")
                  }
                  data-testid="weekly-dm-send"
                >
                  {dmSent ? (
                    <><CheckCircle size={12} weight="fill" /> Sent</>
                  ) : (
                    <><PaperPlaneTilt size={12} weight="fill" /> {dmSending ? "Sending…" : "Send message"}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
